import { Box, Button, Dialog, DialogContent, DialogTitle, Divider, IconButton, Table, TableHead } from '@mui/material';
import { TableBody, TableCell, TableRow } from '@mui/material';
import dayjs from 'dayjs';
import { useFormik } from 'formik';
import { memo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import deviceApi from '~/apis/device';
import facilityApi from '~/apis/facility';
import {
  CheckBoxCircleFillIcon,
  CloseIcon,
  Information2LineIcon,
  InformationFillIcon,
  Notification3Icon,
} from '~/assets/icons';
import BaseRow from '~/components/common/base-row/BaseRow';
import BaseSelect from '~/components/common/base-select/BaseSelect';
import { handleApiError, notify } from '~/utils/helper';
import { IAlertList, ICheckReport, IFacility } from '~/utils/interface/facility';
import EditEquipmentDialog from '../edit-equipment-dialog/EditEquipmentDialog';
import { DIALOG_MODE_CREATE, DIALOG_MODE_EDIT } from '~/utils/constants/common';
import utilityApi from '~/apis/utility';
import CheckReportDialog from '../check-report-dialog/CheckReportDialog';
import DetailEquipmentDialog from '~/pages/admin/equipment/component/detail-equipment-dialog/DetailEquipmentDialog';
import { IEquipment } from '~/utils/interface/equipment';
import DetailAlertDialog from '~/pages/admin/alert/component/detail-alert-dialog/DetailAlertDialog';

type DetailEquipmentDialogProps = {
  open: boolean;
  setOpen?: (open: boolean, needRefreshData?: boolean) => void;
  data?: IFacility;
};

const DetailFacilityDialog = (props: DetailEquipmentDialogProps) => {
  const { t } = useTranslation('admin/facility');
  const [facility, setFacility] = useState<IFacility>();
  const [equipmentList, setEquipmentList] = useState<any>();
  const [alertList, setAlertList] = useState<any>();
  const [reportCheckList, setReportCheckList] = useState<ICheckReport[]>([]);
  const [refreshData, setRefreshData] = useState(false);
  const [isOpenEditEquipmentDialog, setIsOpenEditEquipmentDialog] = useState<boolean>(false);
  const [typeBussinessLabel, setTypeBusinessLable] = useState<string>('');
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [isOpenCheckReportDialog, setIsOpenCheckReportDialog] = useState<boolean>(false);
  const [editEquipmentDialogMode] = useState<string>(DIALOG_MODE_CREATE);
  const [checkReportDialogMode, setCheckReportDialogMode] = useState<string>(DIALOG_MODE_CREATE);
  const [isOpenDetailEquipmentDialog, setIsOpenDetailEquipmentDialog] = useState<boolean>(false);
  const [reportCheck, setReportCheck] = useState<any>();
  const [equipment, setEquipment] = useState<IEquipment>();
  const [isOpenDetailAlertDialog, setIsOpenDetailAlertDialog] = useState<boolean>(false);
  const [selectedAlertId, setSelectedAlertId] = useState<string | null>(null);

  const { data } = props;

  const getStatusStyle = (value: string) => {
    if (Number(value) === 0) {
      return <Box className='text-[#72777B]'>{t('statusOptions.pending')}</Box>;
    } else if (Number(value) === 1) {
      return <Box className='text-[#FF9500] '>{t('statusOptions.deployed')}</Box>;
    } else if (Number(value) === 2) {
      return <Box className='text-[#3D9149]'>{t('statusOptions.activated')}</Box>;
    } else {
      return '';
    }
  };

  const convertLevel = (str: string): string => {
    const match = str.match(/^lv(\d+)$/i);
    if (!match) return str;
    const levelNumber = match[1];
    return `${t('detailFacilityDialog.level')} ${levelNumber}`;
  };

  const getSensorType = (value: string | number) => {
    if (Number(value) === 21) {
      return t('detailFacilityDialog.sensorType.emergencyButton');
    } else if (Number(value) === 17) {
      return t('detailFacilityDialog.sensorType.smokeSensor');
    } else if (Number(value) === 18) {
      return t('detailFacilityDialog.sensorType.heatSensor');
    }
  };

  const getConnectionStatus = (value: string) => {
    if (Number(value) === 0) {
      return (
        <Box className='font-bold text-[#F32A4C] mb-[5px]'>
          {t('detailFacilityDialog.connectionStatus.disconnected')}
        </Box>
      );
    } else if (Number(value) === 1) {
      return (
        <Box className='font-bold text-[#3D9149] mb-[5px]'>{t('detailFacilityDialog.connectionStatus.connected')}</Box>
      );
    }
  };

  const form = useFormik({
    initialValues: {
      periodic_check_cycle: '1',
    },
    onSubmit: async (values) => {
      try {
        const periodicCheckCycleId = values.periodic_check_cycle;
        const periodic_check_cycle = periodicCheckCycleOptions[Number(periodicCheckCycleId)].value;

        const payload = {
          location_id: facility?.location_id,
          periodic_check_cycle,
        };
        await facilityApi.updateCheckCycle(payload);
        notify('success', t('detailFacilityDialog.checkCycle.updateSuccess'));
      } catch (error) {
        handleApiError(error);
      }
    },
  });

  useEffect(() => {
    const facilityId = data?.location_id;
    if (props.open && facilityId) {
      (async () => {
        try {
          const res = await deviceApi.getByInstallation(Number(facilityId));
          setEquipmentList(res.data);
        } catch (error) {
          handleApiError(error);
        }
      })();
      setRefreshData(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps

    if (props.open && facilityId) {
      (async () => {
        try {
          const res = await facilityApi.getListReports(Number(facilityId));
          setReportCheckList(res.data);
        } catch (error) {
          handleApiError(error);
        }
      })();
    }
  }, [refreshData, props.open]);

  useEffect(() => {
    const facilityId = data?.location_id;
    if (props.open && facilityId) {
      (async () => {
        try {
          const res = await facilityApi.getDetailFacility(Number(facilityId));
          const facility = res.data;
          setFacility(facility);
        } catch (error) {
          handleApiError(error);
        }
      })();

      (async () => {
        try {
          const res = await facilityApi.getListAlert(Number(facilityId));
          setAlertList(res.data);
        } catch (error) {
          handleApiError(error);
        }
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.open, refreshData]);

  useEffect(() => {
    (async () => {
      try {
        const response = await utilityApi.getOptionBusiness();
        const typeBusinessOptions = response.data.type_of_business.map((typeBusiness: any) => {
          return {
            id: typeBusiness.id,
            name: typeBusiness.label,
            value: typeBusiness.name,
          };
        });
        const typeBusiness = typeBusinessOptions.find(
          (typeBusiness: any) => typeBusiness.name === facility?.type_business
        );
        if (typeBusiness) {
          setTypeBusinessLable(typeBusiness?.value);
        }
      } catch (error) {
        handleApiError(error);
      }
    })();
  }, [facility]);

  const handleSetOpen = (open: boolean, needRefreshData?: boolean) => {
    if (props.setOpen) {
      props.setOpen(open, needRefreshData);
    }
  };

  const periodicCheckCycleOptions = [
    {
      id: '0',
      value: 6,
      name: t('detailFacilityDialog.checkCycle.sixMonths'),
    },
    {
      id: '1',
      value: 12,
      name: t('detailFacilityDialog.checkCycle.twelveMonths'),
    },
  ];

  const getCheckStatus = (status: number) => {
    switch (status) {
      case 0:
        return t('detailFacilityDialog.listCheckReports.statusOptions.notChecked');
      case 1:
        return t('detailFacilityDialog.listCheckReports.statusOptions.pass');
      case 2:
        return t('detailFacilityDialog.listCheckReports.statusOptions.fail');
      default:
        return '';
    }
  };

  const handleSetIsOpenEditEquipmentDialog = (isOpen: boolean = false, needRefresh: boolean) => {
    setIsOpenEditEquipmentDialog(isOpen);
    setRefreshData(needRefresh);
  };

  const handleSetIsOpenCheckReportDialog = (isOpen: boolean = false, needRefresh: boolean) => {
    setIsOpenCheckReportDialog(isOpen);
    setRefreshData(needRefresh);
  };

  return (
    <>
      <Dialog open={props.open} onClose={() => handleSetOpen(false)} fullWidth maxWidth='lg'>
        <DialogTitle className='bg-[#005F73]'>
          {t('detailFacilityDialog.title')}
          <IconButton onClick={() => handleSetOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent className='!ps-0 !pt-0'>
          <BaseRow>
            <Box className='w-[55%] h-full relative border-r border-solid border-[#DFDFDF]'>
              <Box className='flex flex-row ps-[44px] gap-[15px] mb-[10px] mt-[24px]'>
                <Box className='font-bold'>{t('detailFacilityDialog.locationCode')}:</Box>
                <Box>{facility?.location_code}</Box>
              </Box>

              <Box className='flex flex-row ps-[44px] gap-[15px] mb-[10px]'>
                <Box className='font-bold'>{t('detailFacilityDialog.locationName')}:</Box>
                <Box>{facility?.location_name}</Box>
              </Box>

              <Box className='flex flex-row ps-[44px] gap-[15px] mb-[10px]'>
                <Box className='font-bold'>{t('detailFacilityDialog.province')}:</Box>
                <Box>{facility?.province}</Box>
              </Box>

              <Box className='flex flex-row ps-[44px] gap-[15px] mb-[10px]'>
                <Box className='font-bold'>{t('detailFacilityDialog.ward')}:</Box>
                <Box>{facility?.ward}</Box>
              </Box>

              <Box className='flex flex-row ps-[44px] gap-[15px] mb-[10px]'>
                <Box className='font-bold'>{t('detailFacilityDialog.address')}:</Box>
                <Box>{facility?.address}</Box>
              </Box>

              <Box className='flex flex-row ps-[44px] gap-[15px] mb-[10px]'>
                <Box className='font-bold'>{t('detailFacilityDialog.coordinates')}:</Box>
                <Box>
                  ({facility?.latitude},{facility?.longitude})
                </Box>
              </Box>

              <Box className='flex flex-row ps-[44px] gap-[15px] mb-[10px]'>
                <Box className='font-bold'>{t('detailFacilityDialog.manager')}:</Box>
                <Box>{facility?.manager_name}</Box>
              </Box>

              <Box className='flex flex-row ps-[44px] gap-[15px] mb-[10px]'>
                <Box className='font-bold'>{t('detailFacilityDialog.priorityLevel')}:</Box>
                <Box>{convertLevel(facility?.priority_level ?? '')}</Box>
              </Box>

              <Box className='flex flex-row ps-[44px] gap-[15px] mb-[10px]'>
                <Box className='font-bold min-w-[120px]'>{t('detailFacilityDialog.managerUnit')}:</Box>
                <Box>{facility?.manager_unit}</Box>
              </Box>

              <Box className='flex flex-row ps-[44px] gap-[15px] mb-[10px]'>
                <Box className='font-bold'>{t('detailFacilityDialog.status')}:</Box>
                {getStatusStyle(facility?.status)}
              </Box>

              <Box className='flex flex-col ps-[44px] gap-[8px] mb-[10px]'>
                <Box className='flex flex-row gap-[15px]'>
                  <Box className='font-bold'>{t('detailFacilityDialog.typeBusiness')}:</Box>
                  <Box>{facility?.type_business}</Box>
                </Box>
                <Box className='leading-[120%] text-[#005F73]'>{typeBussinessLabel}</Box>
              </Box>

              <Divider
                sx={{
                  borderColor: '#DFDFDF',
                  borderBottomWidth: 1,
                  width: '100%',
                  mt: 3,
                  mb: 2,
                  height: '100%',
                }}
              ></Divider>

              <Box className='w-full flex flex-col items-center justify-center px-[42px] mb-[20px]'>
                <Box className='w-full flex flex-col gap-[15px]'>
                  <Box className='font-bold text-[#005F73] text-[18px]'>
                    {t('detailFacilityDialog.listCheckReports.title')}
                  </Box>
                  {reportCheckList?.length > 0 ? (
                    <Box className='w-full max-h-[200px] overflow-y-auto'>
                      <Table
                        className='[&_tr]:border-b [&_tr]:border-[#005F73]'
                        sx={{
                          '& .MuiTableCell-root': {
                            borderBottom: 'none',
                          },
                        }}
                      >
                        <TableHead>
                          <TableRow className='border-b border-[#005F73]'>
                            <TableCell align='center' className='font-bold py-[6px] px-0'>
                              {t('detailFacilityDialog.listCheckReports.checkDate')}
                            </TableCell>
                            <TableCell align='center' className='font-bold py-[6px] px-0'>
                              {t('detailFacilityDialog.listCheckReports.checkPerson')}
                            </TableCell>
                            <TableCell align='center' className='font-bold py-[6px] px-0'>
                              {t('detailFacilityDialog.listCheckReports.status')}
                            </TableCell>
                            <TableCell align='center' className='font-bold py-[6px] px-0'>
                              {t('detailFacilityDialog.listCheckReports.checked')}
                            </TableCell>
                          </TableRow>
                        </TableHead>

                        <TableBody>
                          {reportCheckList.map((item: ICheckReport) => (
                            <TableRow
                              key={item.id}
                              className='border-b border-[#005F73] hover:cursor-pointer'
                              onClick={() => {
                                setIsOpenCheckReportDialog(true);
                                setCheckReportDialogMode(DIALOG_MODE_EDIT);
                                setReportCheck(item);
                              }}
                            >
                              <TableCell align='center' className='py-[6px] px-0'>
                                {dayjs(item.check_date).format('DD/MM/YYYY')}
                              </TableCell>
                              <TableCell align='center' className='py-[6px] px-0'>
                                {item.inspector}
                              </TableCell>
                              <TableCell align='center' className='py-[6px] px-0'>
                                {getCheckStatus(item.status)}
                              </TableCell>
                              <TableCell align='center' className='py-[6px] px-0 flex justify-center'>
                                {item.check_status === 1 && item.status === 1 ? (
                                  <CheckBoxCircleFillIcon
                                    fill='#3D9149'
                                    className='lg:h-[15px] xl:h-[25px] lg:w-[15px] xl:w-[25px]'
                                  />
                                ) : item.check_status === 1 && item.status == 2 ? (
                                  <InformationFillIcon
                                    fill='#ED1002'
                                    className='lg:h-[15px] xl:h-[25px] lg:w-[15px] xl:w-[25px]'
                                  />
                                ) : (
                                  <Box className='lg:h-[15px] xl:h-[25px] lg:w-[15px] xl:w-[25px]' />
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </Box>
                  ) : null}
                </Box>
              </Box>

              <Box className='px-[42px]'>
                <Box className='flex items-center justify-between'>
                  <Box>{t('detailFacilityDialog.checkCycle.title')}:</Box>
                  <Box className='w-1/2'>
                    <Box className='w-full flex items-center gap-[15px]'>
                      <BaseSelect
                        size='medium'
                        options={periodicCheckCycleOptions}
                        value={form.values.periodic_check_cycle}
                        placeholder={t('select')}
                        name='periodic_check_cycle'
                        isSearchEnable
                        onChange={(e: any) => {
                          form.setFieldValue('periodic_check_cycle', e.target.value);
                        }}
                        error={Boolean(form.errors?.periodic_check_cycle)}
                        helperText={form.errors?.periodic_check_cycle}
                        displayInvalidValue
                      />
                      <Button
                        className='text-white bg-[#005F73] hover:bg-[#005F73]'
                        onClick={() => form.handleSubmit()}
                      >
                        {t('save')}
                      </Button>
                    </Box>
                  </Box>
                </Box>
                <Box className='flex items-center justify-between py-[15px]'>
                  <Box>{t('detailFacilityDialog.checkCycle.lastCheck')}</Box>
                  <Box>{facility?.last_check_date && dayjs(facility?.last_check_date).format('DD/MM/YYYY')}</Box>
                </Box>
                <Box className='flex items-center justify-between'>
                  <Box>{t('detailFacilityDialog.checkCycle.nextCheck')}</Box>
                  <Box className='flex items-center justify-between gap-[30px]'>
                    {facility?.check_due_status === 1 && (
                      <Box className='text-[#ED1002] font-semibold flex items-center gap-4'>
                        <Notification3Icon />
                        <Box> {t('detailFacilityDialog.checkCycle.almostDue')}</Box>
                      </Box>
                    )}
                    {facility?.check_due_status === 2 && (
                      <Box className='text-[#ED1002] font-semibold flex items-center gap-4'>
                        <Notification3Icon />
                        <Box> {t('detailFacilityDialog.checkCycle.overdue')}</Box>
                      </Box>
                    )}
                    <Box>{facility?.next_check_date && dayjs(facility?.next_check_date).format('DD/MM/YYYY')}</Box>
                  </Box>
                </Box>
                <Box className='flex items-center justify-between py-[15px]'>
                  <Box>{t('detailFacilityDialog.checkCycle.status')}</Box>
                  <Box className='font-bold text-[#02AB75]'>
                    {facility?.pass === 0
                      ? t('detailFacilityDialog.checkCycle.skipped')
                      : t('detailFacilityDialog.checkCycle.checked')}
                  </Box>
                </Box>
                <Box className='w-full flex justify-end mt-[15px]'>
                  <Button
                    className='bg-[#ED1002] hover:bg-[#ED1002] text-white w-[120px] h-[35px] font-bold '
                    onClick={() => {
                      setIsOpenCheckReportDialog(true);
                      setCheckReportDialogMode(DIALOG_MODE_CREATE);
                    }}
                  >
                    {t('report')}
                  </Button>
                </Box>
              </Box>
            </Box>

            <Box className='w-[45%] mt-[24px] ml-[24px]'>
              <Box className='mb-[36px]'>
                <BaseRow className='mb-[13px] gap-[15px] w-full !justify-between !items-center'>
                  <Box className='font-bold text-[#005F73] text-[18px]'>{t('detailFacilityDialog.equipmentList')}</Box>
                  <Box className='flex items-center'>
                    <Box className='rounded-full text-white w-[25px] h-[25px] flex items-center justify-center font-bold bg-[#005F73] p-2'>
                      {equipmentList?.length}
                    </Box>
                  </Box>
                </BaseRow>
                {equipmentList?.length ? (
                  <Box className='rounded-[10px] max-h-[350px] overflow-y-auto border-[1px] border-[#005F73]'>
                    <Table>
                      <TableBody className='divide-y divide-[#005F73]'>
                        {equipmentList.map((row: any, index: any) => {
                          return (
                            <TableRow key={index}>
                              <TableCell className='border-none'>
                                <Box>
                                  <Box className='font-bold mb-[5px]'>{row.device_code}</Box>
                                  <Box>{row.sim_no}</Box>
                                </Box>
                              </TableCell>
                              <TableCell className='border-none text-right flex items-center justify-end gap-[10px]'>
                                <Box className='flex flex-col items-end'>
                                  <Box className='mb-[5px]'>{row.device_name}</Box>
                                  {getConnectionStatus(row.connect_status)}
                                </Box>
                                <IconButton>
                                  <Information2LineIcon
                                    fill='#72777B'
                                    onClick={() => {
                                      setIsOpenDetailEquipmentDialog(true);
                                      setEquipment(row);
                                    }}
                                  />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </Box>
                ) : null}
              </Box>
              <Box className='mb-[26px]'>
                <BaseRow className='mb-[13px] gap-[15px] w-full !justify-between !items-center'>
                  <Box className='font-bold text-[#ED1002] text-[18px]'>{t('detailFacilityDialog.historyAlert')}</Box>
                </BaseRow>
                {alertList?.length ? (
                  <Box className='rounded-[10px] max-h-[350px] overflow-y-auto border-[1px] border-[#005F73]'>
                    <Table>
                      <TableBody className='divide-y divide-[#005F73]'>
                        {alertList.map((row: IAlertList, index: any) => {
                          return (
                            <TableRow key={index}>
                              <TableCell className='border-none'>
                                <Box>
                                  <Box className='font-bold mb-[5px]'>{row.device_code}</Box>
                                  <Box>{getSensorType(row.sensor_type)}</Box>
                                </Box>
                              </TableCell>
                              <TableCell className='border-none text-right flex items-center justify-end gap-[10px]'>
                                <Box className='flex flex-col items-center'>
                                  <Box className='mb-[5px]'>{dayjs(row.created_at).format('DD/MM/YYYY HH:mm')}</Box>

                                  <Box
                                    className={` border rounded-[2em] font-bold w-[120px] text-center ml-auto ${
                                      row.status === 0
                                        ? 'border-[#ED1002] text-[#ED1002]'
                                        : 'border-[#3D9149] text-[#3D9149]'
                                    }`}
                                  >
                                    {row.status === 0 ? 'Chờ xác nhận' : 'Đã xác nhận'}
                                  </Box>
                                </Box>
                                <IconButton>
                                  <Information2LineIcon
                                    fill='#72777B'
                                    onClick={() => {
                                      setIsOpenDetailAlertDialog(true);
                                      setSelectedAlertId(row.id ?? '');
                                    }}
                                  />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </Box>
                ) : null}
              </Box>
            </Box>
          </BaseRow>

          <EditEquipmentDialog
            locationId={facility?.location_id}
            open={isOpenEditEquipmentDialog}
            mode={editEquipmentDialogMode}
            setOpen={handleSetIsOpenEditEquipmentDialog}
          />

          <CheckReportDialog
            locationId={facility?.location_id}
            open={isOpenCheckReportDialog}
            setOpen={handleSetIsOpenCheckReportDialog}
            mode={checkReportDialogMode}
            data={reportCheck}
          />

          <DetailEquipmentDialog
            open={isOpenDetailEquipmentDialog}
            setOpen={setIsOpenDetailEquipmentDialog}
            data={equipment}
          />

          <DetailAlertDialog
            open={isOpenDetailAlertDialog}
            data={{ id: selectedAlertId }}
            setOpen={setIsOpenDetailAlertDialog}
          />
        </DialogContent>
      </Dialog>
    </>
  );
};

export default memo(DetailFacilityDialog);
